import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TAPIKeyHistoryListing, TSettingReduxState } from '~types/redux/state';
import {
    TAPIKeySettingsReduxResponse,
    TCreateDiscountsSettingsReduxParams,
    TDiscountDetailsSettings,
    TDiscountListingFilters,
    TGetDiscountSettingsAPIResponse,
    TGetSettingsAPIResponse,
    TGlobalAndProductSettings,
    TUpdateDiscountsSettingsReduxParams,
    TUpdateGlobalAndProductSettingsReduxParams,
} from '~types/feat/setting';
import { AdminSettingsTypeEnum, SettingTabsEnum, TransactionStatusEnum } from '~utils/enums';

export const initialState: TSettingReduxState = {
    loadings: {
        getSettings: false,
        updateSettings: false,
        createProduct: false,
        getApiKeySettings: false,
        updateApiKeySettings: false,
        getDiscounts: false,
        updateDiscount: false,
        createDiscount: false,
        getAllDiscount: false,
        getApiKeyHistoryListing: false,
        getSilverApiKeySettings: false,
        updateSilverApiKeySettings: false,
        getSilverApiKeyHistoryListing: false,
    },
    selectedTab: SettingTabsEnum.APIKey,
    modals: {
        productSettingDetailsModalIsOpen: false,
        productSettingDetailsModalIsEditing: false,
        productSettingDeleteProductModalIsOpen: false,
        productSettingCreateProductModalIsOpen: false,
        productManagementDiscountCreateModalIsOpen: false,
        productManagementDiscountDetailsModalIsOpen: false,
        productManagementDiscountDetailsModalIsEditing: false,
        productManagementDiscountDeleteModalIsOpen: false,
        regenerateAPIKeyModalIsOpen: false,
        regenerateSilverAPIKeyModalIsOpen: false,
    },
    settings: {
        index: 0,
        maxIndex: 0,
        globalSettingsIsEditing: false,
        filters: {
            productName: '',
            fundType: '',
            goldType: '',
            metalType: '',
        },
        appliedMetalType: '',
        data: [],
    },
    apiKeySettings: {
        apiKey: '',
        expiryDate: '',
    },
    apiKeyHistoryListing: {
        index: 0,
        maxIndex: 0,
        data: [],
    },
    silverApiKeySettings: {
        apiKey: '',
        expiryDate: '',
    },
    silverApiKeyHistoryListing: {
        index: 0,
        maxIndex: 0,
        data: [],
    },
    productSettingDetails: {
        id: 0,
        type: AdminSettingsTypeEnum.Product,
        productId: undefined,
        status: TransactionStatusEnum.Pending,
        tttBankBuySplitPercentage: '',
        affinBankBuySplitPercentage: '',
        tttBankSellSplitPercentage: '',
        affinBankSellSplitPercentage: '',
        mintingCostPercentage: '',
        tttBankBuySplit: '',
        affinBankBuySplit: '',
        tttBankSellSplit: '',
        affinBankSellSplit: '',
        mintingCost: '',
        lowGoldAlert: '',
        isEditable: false,
        createdAt: '',
        updatedAt: '',
        emails: [],
    },
    discounts: {
        index: 0,
        maxIndex: 0,
        data: [],
        filters: {
            discountCode: '',
            discountName: '',
            discountType: '',
            transactionType: '',
            status: '',
        },
        selectedDiscount: undefined,
    },
    allDiscounts: [],
    isLoadingCreateAdminSettings: false,
    createAdminSettingsError: '',
    errors: {
        getSettings: '',
        updateSettings: '',
        createProduct: '',
        getApiKeySettings: '',
        updateApiKeySettings: '',
        getDiscounts: '',
        updateDiscount: '',
        createDiscount: '',
        getAllDiscount: '',
        getApiKeyHistoryListing: '',
        getSilverApiKeySettings: '',
        updateSilverApiKeySettings: '',
        getSilverApiKeyHistoryListing: '',
    },
};

const settingSlice = createSlice({
    name: 'setting',
    initialState,
    reducers: {
        settingSetGetSettingsIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.settings.index = action.payload.index;
        },
        settingSetGetSettingsFilters: (state, action: PayloadAction<{ productName: string }>) => {
            state.settings.filters = action.payload;
        },
        settingClearGetSettingsFilters: (state) => {
            state.settings.filters = initialState.settings.filters;
            state.settings.appliedMetalType = '';
        },
        settingSetGlobalSettingsIsEditing: (state, action: PayloadAction<boolean>) => {
            state.settings.globalSettingsIsEditing = action.payload;
        },
        settingSetSettingsData: (state, action: PayloadAction<TGlobalAndProductSettings[]>) => {
            state.settings.data = action.payload;
        },
        settingGetSettingsAttempt: (state) => {
            state.loadings.getSettings = true;
            state.errors.getSettings = '';
            state.settings.appliedMetalType = state.settings.filters.metalType ?? '';
        },
        settingGetSettingsSuccess: (state, action: PayloadAction<TGetSettingsAPIResponse>) => {
            state.loadings.getSettings = false;
            state.settings = {
                ...state.settings,
                ...action.payload,
            };
        },
        settingGetSettingsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSettings = false;
            if (action.payload) state.errors.getSettings = action.payload;
        },
        settingSetProductSettingDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.productSettingDetailsModalIsOpen = action.payload;
        },
        settingSetProductSettingDetailsModalIsEditing: (state, action: PayloadAction<boolean>) => {
            state.modals.productSettingDetailsModalIsEditing = action.payload;
        },
        settingSetProductSettingDeleteProductModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.productSettingDeleteProductModalIsOpen = action.payload;
        },
        settingsResetUpdateSettingsState: (state) => {
            state.loadings.updateSettings = initialState.loadings.updateSettings;
            state.errors.updateSettings = initialState.errors.updateSettings;
        },

        settingUpdateSettingsAttempt: (state, _action: PayloadAction<TUpdateGlobalAndProductSettingsReduxParams>) => {
            state.loadings.updateSettings = true;
            state.errors.updateSettings = '';
        },
        settingUpdateSettingsSuccess: (state) => {
            state.loadings.updateSettings = false;
        },
        settingUpdateSettingsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateSettings = false;
            if (action.payload) state.errors.updateSettings = action.payload;
        },
        settingResetUpdateSettingsState: (state) => {
            state.loadings.updateSettings = initialState.loadings.updateSettings;
            state.errors.updateSettings = initialState.errors.updateSettings;
        },
        settingSetProductSettingDetails: (state, action: PayloadAction<TGlobalAndProductSettings>) => {
            state.productSettingDetails = action.payload;
        },
        settingsSetSelectedTab: (state, action: PayloadAction<SettingTabsEnum>) => {
            state.selectedTab = action.payload;
        },
        settingGetAPIKeySettingsAttempt: (state) => {
            state.loadings.getApiKeySettings = true;
            state.errors.getApiKeySettings = '';
        },
        settingGetAPIKeySettingsSuccess: (state, action: PayloadAction<TAPIKeySettingsReduxResponse>) => {
            state.loadings.getApiKeySettings = false;
            state.apiKeySettings = action.payload;
        },
        settingGetAPIKeySettingsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getApiKeySettings = false;
            if (action.payload) state.errors.getApiKeySettings = action.payload;
        },
        settingSetRegenerateAPIKeyModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.regenerateAPIKeyModalIsOpen = action.payload;
        },
        settingUpdateAPIKeySettingsAttempt: (state) => {
            state.loadings.updateApiKeySettings = true;
            state.errors.updateApiKeySettings = '';
        },
        settingUpdateAPIKeySettingsSuccess: (state) => {
            state.loadings.updateApiKeySettings = false;
        },
        settingUpdateAPIKeySettingsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateApiKeySettings = false;
            if (action.payload) state.errors.updateApiKeySettings = action.payload;
        },
        settingSetGetAPIKeyHistoryListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.apiKeyHistoryListing.index = action.payload.index;
        },
        settingGetAPIKeyHistoryListingAtempt: (state) => {
            state.loadings.getApiKeyHistoryListing = true;
            state.errors.getApiKeyHistoryListing = '';
        },
        settingGetAPIKeyHistoryListingSuccess: (state, action: PayloadAction<TAPIKeyHistoryListing>) => {
            state.loadings.getApiKeyHistoryListing = false;
            state.apiKeyHistoryListing = action.payload;
        },
        settingGetAPIKeyHistoryListingFailure: (state, action: PayloadAction<string | undefined>) =>{
            state.loadings.getApiKeyHistoryListing = false;
            if (action.payload) state.errors.getApiKeyHistoryListing = action.payload;
        },
        settingGetSilverAPIKeySettingsAttempt: (state) => {
            state.loadings.getSilverApiKeySettings = true;
            state.errors.getSilverApiKeySettings = '';
        },
        settingGetSilverAPIKeySettingsSuccess: (state, action: PayloadAction<TAPIKeySettingsReduxResponse>) => {
            state.loadings.getSilverApiKeySettings = false;
            state.silverApiKeySettings = action.payload;
        },
        settingGetSilverAPIKeySettingsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSilverApiKeySettings = false;
            if (action.payload) state.errors.getSilverApiKeySettings = action.payload;
        },
        settingSetRegenerateSilverAPIKeyModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.regenerateSilverAPIKeyModalIsOpen = action.payload;
        },
        settingUpdateSilverAPIKeySettingsAttempt: (state) => {
            state.loadings.updateSilverApiKeySettings = true;
            state.errors.updateSilverApiKeySettings = '';
        },
        settingUpdateSilverAPIKeySettingsSuccess: (state) => {
            state.loadings.updateSilverApiKeySettings = false;
        },
        settingUpdateSilverAPIKeySettingsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateSilverApiKeySettings = false;
            if (action.payload) state.errors.updateSilverApiKeySettings = action.payload;
        },
        settingSetGetSilverAPIKeyHistoryListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.silverApiKeyHistoryListing.index = action.payload.index;
        },
        settingGetSilverAPIKeyHistoryListingAttempt: (state) => {
            state.loadings.getSilverApiKeyHistoryListing = true;
            state.errors.getSilverApiKeyHistoryListing = '';
        },
        settingGetSilverAPIKeyHistoryListingSuccess: (state, action: PayloadAction<TAPIKeyHistoryListing>) => {
            state.loadings.getSilverApiKeyHistoryListing = false;
            state.silverApiKeyHistoryListing = action.payload;
        },
        settingGetSilverAPIKeyHistoryListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSilverApiKeyHistoryListing = false;
            if (action.payload) state.errors.getSilverApiKeyHistoryListing = action.payload;
        },
        settingSetProductManagementDiscountDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.productManagementDiscountDetailsModalIsOpen = action.payload;
        },
        settingSetProductManagementDiscountDetailsModalIsEditing: (state, action: PayloadAction<boolean>) => {
            state.modals.productManagementDiscountDetailsModalIsEditing = action.payload;
        },
        settingSetProductManagementDiscountCreateModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.productManagementDiscountCreateModalIsOpen = action.payload;
        },
        settingSetProductManagementDiscountDeleteModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.productManagementDiscountDeleteModalIsOpen = action.payload;
        },
        settingSetDiscountSettingDetails: (state, action: PayloadAction<TDiscountDetailsSettings>) => {
            state.discounts.selectedDiscount = action.payload;
        },

        settingSetDiscountsListingFilters: (state, action: PayloadAction<TDiscountListingFilters>) => {
            state.discounts.filters = action.payload;
        },
        settingClearDiscountsListingFilters: (state) => {
            state.discounts.filters = initialState.discounts.filters;
        },
        settingGetDiscountsAttempt: (state) => {
            state.loadings.getDiscounts = true;
            state.errors.getDiscounts = '';
        },
        settingGetDiscountsSuccess: (state, action: PayloadAction<TGetDiscountSettingsAPIResponse>) => {
            state.loadings.getDiscounts = false;
            state.discounts = {
                ...state.discounts,
                ...action.payload,
            };
        },
        settingGetDiscountsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getDiscounts = false;
            if (action.payload) state.errors.getDiscounts = action.payload;
        },
        settingCreateDiscountAttempt: (state, _action: PayloadAction<TCreateDiscountsSettingsReduxParams>) => {
            state.loadings.createDiscount = true;
            state.errors.createDiscount = '';
        },
        settingCreateDiscountSuccess: (state, action: PayloadAction<string | null>) => {
            state.loadings.createDiscount = false;
            if (action.payload) state.errors.createDiscount = action.payload;
        },
        settingCreateDiscountFailure: (state, action: PayloadAction<string | null>) => {
            state.loadings.createDiscount = true;
            if (action.payload) state.errors.createDiscount = action.payload;
        },
        settingUpdateDiscountAttempt: (state, _action: PayloadAction<TUpdateDiscountsSettingsReduxParams>) => {
            state.loadings.updateDiscount = true;
            state.errors.updateDiscount = '';
        },
        settingUpdateDiscountSuccess: (state, action: PayloadAction<string | null>) => {
            state.loadings.updateDiscount = false;
            if (action.payload) state.errors.updateDiscount = action.payload;
        },
        settingUpdateDiscountFailure: (state, action: PayloadAction<string | null>) => {
            state.loadings.updateDiscount = true;
            if (action.payload) state.errors.updateDiscount = action.payload;
        },
        settingSetGetDiscountSettingsIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.discounts.index = action.payload.index;
        },
        settingGetAllDiscountAttempt: (state) => {
            state.loadings.getAllDiscount = true;
            state.errors.getAllDiscount = '';
        },
        settingGetAllDiscountSuccess: (state, action: PayloadAction<TDiscountDetailsSettings[]>) => {
            state.loadings.getAllDiscount = false;
            state.allDiscounts = action.payload;
        },
        settingGetAllDiscountFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllDiscount = false;
            if (action.payload) state.errors.getAllDiscount = action.payload;
        },
        settingSetProductSettingCreateProductModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.productSettingCreateProductModalIsOpen = action.payload;
        },
        settingProductSettingCreateProductAttempt: (state, _action: PayloadAction<TUpdateGlobalAndProductSettingsReduxParams>) => {
            state.loadings.createProduct = true;
            state.errors.createProduct = '';
        },
        settingProductSettingCreateProductSuccess: (state, action: PayloadAction<string | null>) => {
            state.loadings.createProduct = false;
            if (action.payload) state.errors.createProduct = action.payload;
        },
        settingProductSettingCreateProductFailure: (state, action: PayloadAction<string | null>) => {
            state.loadings.createProduct = false;
            if (action.payload) state.errors.createProduct = action.payload;
        },

        settingCreateAdminSettingsAttempt: (state, _action: PayloadAction<TUpdateGlobalAndProductSettingsReduxParams>) => {
            state.isLoadingCreateAdminSettings = true;
            state.createAdminSettingsError = '';
        },
        settingCreateAdminSettingsSuccess: (state) => {
            state.isLoadingCreateAdminSettings = false;
        },
        settingCreateAdminSettingsFailure: (state, action: PayloadAction<string>) => {
            state.isLoadingCreateAdminSettings = false;
            state.createAdminSettingsError = action.payload;
        },
    },
});

export default {
    actions: settingSlice.actions,
    reducers: settingSlice.reducer,
};
